import logger from './logger';
/**
 * URL Sanitizer Utility
 * Provides functions to validate and sanitize URLs to prevent XSS and Open Redirect vulnerabilities
 */

/**
 * Validates if a URL is from an allowed domain (Firebase Storage or app domain)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is safe, false otherwise
 */
export const isAllowedUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    // Allow Firebase Storage URLs
    const allowedDomains = [
      'firebasestorage.googleapis.com',
      'storage.googleapis.com',
    ];

    // Also allow relative URLs (starting with /)
    if (url.startsWith('/')) {
      return true;
    }

    // Check if the URL uses a safe protocol
    const allowedProtocols = ['https:', 'http:'];
    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return false;
    }

    // Check if domain is allowed
    return allowedDomains.some(domain => parsedUrl.hostname.includes(domain));
  } catch (error) {
    // Invalid URL
    return false;
  }
};

/**
 * Sanitizes a URL for use in img src attributes
 * @param {string} url - The URL to sanitize
 * @returns {string} - The sanitized URL or a placeholder
 */
export const sanitizeImageUrl = (url) => {
  if (!url) {
    return '';
  }

  const trimmedUrl = url.trim();

  // Validate against allowed domains and safe protocols
  if (!isAllowedUrl(trimmedUrl)) {
    logger.warn('URL not from allowed domain or protocol:', url);
    return '';
  }

  return trimmedUrl;
};

/**
 * Validates a URL before using it in window.open or redirects
 * @param {string} url - The URL to validate
 * @returns {string|null} - The validated URL or null if invalid
 */
export const validateRedirectUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmedUrl = url.trim();

  // Only allow URLs from Firebase Storage or relative paths using safe protocols
  if (!isAllowedUrl(trimmedUrl)) {
    logger.warn('Blocked redirect to non-allowed domain or protocol:', url);
    return null;
  }

  return trimmedUrl;
};

/**
 * Safely open a URL in a new window after validation.
 * @param {string} url - The URL to open
 * @returns {boolean} - True if opened, false otherwise
 */
export const openSafeUrl = (url) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const validatedUrl = validateRedirectUrl(url);
  if (!validatedUrl) {
    return false;
  }

  const resolvedUrl = validatedUrl.startsWith('http')
    ? validatedUrl
    : new URL(validatedUrl, window.location.origin).toString();

  window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * Sanitizes an external URL (website, LinkedIn, social, portfolio) for use in
 * an href attribute. Allows https/http/mailto/tel; blocks javascript: and all
 * other dangerous protocols.
 * @param {string} url
 * @returns {string} — safe URL or empty string
 */
export const sanitizeHref = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  try {
    const parsed = new URL(trimmed);
    if (['https:', 'http:', 'mailto:', 'tel:'].includes(parsed.protocol)) {
      return trimmed;
    }
    return '';
  } catch {
    // Not a full URL — could be a relative path or bare handle
    if (trimmed.startsWith('/') || trimmed.startsWith('./')) return trimmed;
    return '';
  }
};

/**
 * @param {string} text - The text to escape
 * @returns {string} - The escaped text
 */
export const escapeHtml = (text) => {
  if (!text) return '';

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };

  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

/**
 * Sanitizes text input by trimming whitespace and enforcing a max length.
 * Use before saving user-generated content to Firestore.
 * @param {string} text - The text to sanitize
 * @param {number} maxLength - Maximum allowed length (default 1000)
 * @returns {string} - The sanitized text
 */
export const sanitizeText = (text, maxLength = 1000) => {
  if (!text || typeof text !== 'string') return '';
  return text.trim().slice(0, maxLength);
};
